import { AudioManager } from './AudioManager';

/**
 * ハンガー（機体選択画面）BGM
 * テンポ: 90BPM、落ち着いたアンビエント＋ミリタリー風
 *
 * 構成:
 *   - パッド（低域持続和音）
 *   - アルペジオ（ゆっくりした音列）
 *   - パルス（控えめなリズム感）
 */
export class HangarBGM {
  private am: AudioManager;
  private gainNode: GainNode;
  private scheduledIds: ReturnType<typeof setTimeout>[] = [];
  private running = false;
  private loopStartTime = 0;

  // 90 BPM
  private readonly BPM  = 90;
  private readonly BEAT = 60 / this.BPM;
  private readonly BAR  = this.BEAT * 4;
  private readonly LOOP = this.BAR * 8; // 8小節ループ

  private readonly NOTES: Record<string, number> = {
    C2: 65.4,  D2: 73.4, Eb2: 77.8, F2: 87.3,
    G2: 98.0, Ab2: 103.8, Bb2: 116.5,
    C3: 130.8, Eb3: 155.6, F3: 174.6, G3: 196.0, Ab3: 207.7, Bb3: 233.1,
    C4: 261.6, Eb4: 311.1, F4: 349.2, G4: 392.0,
  };

  constructor(am: AudioManager) {
    this.am = am;
    this.gainNode = am.createGain(0.45);
    this.gainNode.connect(am.master);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const startDelay = Math.max(this.am.ctx.currentTime + 0.1, 0.1);
    this.loopStartTime = startDelay;
    this.scheduleLoop(this.loopStartTime);
  }

  stop(): void {
    this.running = false;
    this.scheduledIds.forEach(id => clearTimeout(id));
    this.scheduledIds = [];
    this.gainNode.gain.setTargetAtTime(0, this.am.now, 0.8);
  }

  private scheduleLoop(startTime: number): void {
    this.schedulePad(startTime);
    this.scheduleArpeggio(startTime);
    this.schedulePulse(startTime);

    const msUntilNext = (startTime + this.LOOP - this.am.ctx.currentTime - 0.2) * 1000;
    const id = setTimeout(() => {
      if (!this.running) return;
      this.loopStartTime = startTime + this.LOOP;
      this.scheduleLoop(this.loopStartTime);
    }, Math.max(0, msUntilNext));
    this.scheduledIds.push(id);
  }

  // ────────────────────────────────────────────
  // パッド（持続和音 — 温かみのある低域）
  // ────────────────────────────────────────────
  private schedulePad(t0: number): void {
    const half = this.LOOP / 2;
    // 前半: Cm (C, Eb, G)
    this.playPadTone(t0, half, this.NOTES['C2']!, 0.10);
    this.playPadTone(t0, half, this.NOTES['Eb2']!, 0.06);
    this.playPadTone(t0, half, this.NOTES['G2']!, 0.05);
    // 後半: Ab (Ab, C, Eb)
    this.playPadTone(t0 + half, half, this.NOTES['Ab2']!, 0.08);
    this.playPadTone(t0 + half, half, this.NOTES['C3']!, 0.05);
    this.playPadTone(t0 + half, half, this.NOTES['Eb3']!, 0.04);
  }

  private playPadTone(t: number, dur: number, freq: number, vol: number): void {
    const ctx = this.am.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;

    const osc2 = ctx.createOscillator();
    osc2.type = 'triangle';
    osc2.frequency.value = freq * 1.002; // 微妙なデチューン

    const lp = this.am.createFilter('lowpass', freq * 5, 0.5);
    const g = this.am.createGain(0);
    osc.connect(lp);
    osc2.connect(lp);
    lp.connect(g);
    g.connect(this.gainNode);

    osc.start(t); osc.stop(t + dur + 0.5);
    osc2.start(t); osc2.stop(t + dur + 0.5);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 1.0);
    g.gain.setValueAtTime(vol, t + dur - 1.0);
    g.gain.linearRampToValueAtTime(0, t + dur);
  }

  // ────────────────────────────────────────────
  // アルペジオ（ゆっくりした音列）
  // ────────────────────────────────────────────
  private scheduleArpeggio(t0: number): void {
    const b = this.BEAT;
    // ゆったりとしたアルペジオパターン
    const pattern: [number, string, number][] = [
      // bar 1-2
      [0,   'C4',  1.5], [2,   'Eb4', 1.5], [4,   'G3',  1.5], [6,   'F3',  1.5],
      // bar 3-4
      [8,   'Eb4', 1.5], [10,  'C4',  1.5], [12,  'G3',  2],   [14,  'Bb3', 2],
      // bar 5-6
      [16,  'Ab3', 1.5], [18,  'C4',  1.5], [20,  'Eb4', 1.5], [22,  'C4',  1.5],
      // bar 7-8
      [24,  'G3',  1.5], [26,  'Ab3', 1.5], [28,  'G3',  2],   [30,  'Eb3', 2],
    ];

    for (const [beat, note, len] of pattern) {
      const freq = this.NOTES[note];
      if (!freq) continue;
      this.playArpNote(t0 + beat * b, len * b, freq);
    }
  }

  private playArpNote(t: number, dur: number, freq: number): void {
    const ctx = this.am.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;

    const lp = this.am.createFilter('lowpass', freq * 3, 0.8);
    const g = this.am.createGain(0);
    osc.connect(lp);
    lp.connect(g);
    g.connect(this.gainNode);

    osc.start(t); osc.stop(t + dur + 0.1);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.08, t + 0.05);
    g.gain.setValueAtTime(0.06, t + dur * 0.5);
    g.gain.linearRampToValueAtTime(0, t + dur);
  }

  // ────────────────────────────────────────────
  // パルス（控えめなリズム）
  // ────────────────────────────────────────────
  private schedulePulse(t0: number): void {
    const b = this.BEAT;
    // 2拍ごとの控えめな低音パルス
    for (let i = 0; i < 32; i += 2) {
      this.playPulse(t0 + i * b, i % 8 === 0 ? 0.12 : 0.07);
    }
  }

  private playPulse(t: number, vol: number): void {
    const ctx = this.am.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(60, t);
    osc.frequency.exponentialRampToValueAtTime(35, t + 0.15);

    const g = this.am.createGain(0);
    osc.connect(g);
    g.connect(this.gainNode);
    osc.start(t); osc.stop(t + 0.25);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
  }
}
